import { ConcurrentOrganization, Passport } from '@/types/api';
import { sortOrganizationPassports } from './sort';

/**
 * 표시 관점이 확정된 Passport.
 *
 * `isConcurrent` 가 boolean 으로 좁혀져 있어, 아직 관점이 정해지지 않은 원본
 * Passport(`isConcurrent?: boolean`)와 타입 레벨에서 구분된다.
 * 직책 배지 계산(`getPositionBadges`)은 이 타입만 받으므로, resolve 를 거치지 않은
 * Passport 를 그대로 넘기면 컴파일 단계에서 막힌다.
 */
export type ResolvedPassport = Passport & { isConcurrent: boolean };

/**
 * 겸직 소속 정보(ConcurrentOrganization)를 본인 프로필에 덮어씌워
 * '겸직 조직에 렌더링되는 카드'용 Passport 를 생성합니다.
 *
 * - 소속 정보(부서/조직/회사)는 본 소속이 아니라 겸직 소속 값을 사용합니다.
 *   `organizationCode` 도 함께 덮이므로, 변환 후에는 조직 코드 비교로 겸직 여부를
 *   되물을 수 없습니다. 그래서 `isConcurrent=true` 를 따로 남깁니다.
 * - 직책(팀장/팀장대행/파트장)은 본 소속이 아니라 해당 겸직 원소의 값을 따릅니다.
 *   본 소속 배지가 겸직 카드로 새지 않도록 해당 조직의 직책으로 표시 범위를 좁힙니다.
 *
 * @param passport 본인 프로필 카드
 * @param concurrent 겸직 소속 원소
 * @returns 겸직 소속 기준으로 재구성된 Passport
 */
function buildConcurrentPassport(
  passport: Passport,
  concurrent: ConcurrentOrganization,
): ResolvedPassport {
  return {
    ...passport,
    isConcurrent: true,
    detail: {
      ...passport.detail,
      departmentId: concurrent.departmentId,
      departmentCode: concurrent.departmentCode,
      departmentName: concurrent.departmentName,
      divisionCode: concurrent.divisionCode,
      divisionName: concurrent.divisionName,
      companyCode: concurrent.companyCode,
      companyName: concurrent.companyName,
      organizationCode: concurrent.organizationCode,
      organizationFullName: concurrent.organizationFullName,
      organizationShortName: concurrent.organizationShortName,
      isLeader: concurrent.isLeader,
      isPartLeader: concurrent.isPartLeader,
      isActingTeamLeader: concurrent.isActingTeamLeader,
    },
  };
}

/**
 * 특정 조직(orgCode) 노드 안에서 카드를 보는 관점으로 해석합니다.
 *
 * - 본 소속이 orgCode 이면 본 소속 기준(`isConcurrent=false`).
 * - orgCode 가 겸직 소속 중 하나이면 겸직 소속 기준 카드로 변환(`isConcurrent=true`).
 * - 둘 다 아니면 본 소속 기준 그대로.
 *
 * 조직 하나를 조회해 그리는 화면(조직도 리스트뷰, 모바일 조직도)에서 사용합니다.
 * 조직별 조회에는 본 소속이 다른 겸직자가 함께 내려오므로, 이 변환을 거쳐야
 * 소속 텍스트와 직책이 현재 보고 있는 조직 기준으로 표시됩니다.
 *
 * @param passport 대상 Passport
 * @param orgCode 렌더링 기준 조직 코드
 */
export function resolveInOrganization(
  passport: Passport,
  orgCode: string,
): ResolvedPassport {
  // 이미 관점이 확정된 카드(차트뷰 expandConcurrentPassports 결과 등)는 그대로 둔다.
  // 겸직 카드는 organizationCode 가 덮여 있어 여기서 다시 비교하면 겸직 여부를 잃는다.
  if (passport.isConcurrent !== undefined) {
    return passport as ResolvedPassport;
  }

  if (passport.detail.organizationCode === orgCode) {
    return { ...passport, isConcurrent: false };
  }

  const concurrent = passport.detail.concurrentOrganizations?.find(
    (c) => c.organizationCode === orgCode,
  );

  return concurrent
    ? buildConcurrentPassport(passport, concurrent)
    : { ...passport, isConcurrent: false };
}

/**
 * 조직 맥락 없이 인물 자체를 보는 관점으로 해석합니다.
 *
 * 소속과 직책은 본 소속 기준을 그대로 쓰고, 실제 겸직 소속 보유 여부를 남깁니다.
 * 본직 직책 레이블은 detail의 직책 값을 사용하며, 겸직 여부는 별도로 판정합니다.
 *
 * 기준 조직이 없는 화면(인사조회 전사검색, 프로필 카드)에서 사용합니다.
 *
 * @param passport 대상 Passport
 */
export function resolveAsPerson(passport: Passport): ResolvedPassport {
  if (passport.isConcurrent !== undefined) {
    return passport as ResolvedPassport;
  }

  return {
    ...passport,
    isConcurrent: (passport.detail.concurrentOrganizations?.length ?? 0) > 0,
  };
}

/**
 * Passport 배열을 organizationCode 기준으로 그룹화하여 Map 으로 반환합니다.
 *
 * 본 소속뿐 아니라 `detail.concurrentOrganizations` 의 각 겸직 조직에도 해당
 * 임직원을 추가로 렌더링합니다. 한 사람이 본 소속 1장 + 겸직 N장으로 늘어나므로,
 * 트리 전체를 한 번에 그리는 조직도 차트뷰에서 사용합니다.
 * (조직 하나만 보는 화면은 `resolveInOrganization` 으로 충분합니다.)
 *
 * 동일 임직원이 같은 조직에 중복으로 들어가지 않도록 passportId 로 방어합니다.
 *
 * @param passports Passport 배열
 * @returns 조직 코드를 키로 갖는 Map
 */
export function expandConcurrentPassports(
  passports: Passport[] = [],
): Map<string, ResolvedPassport[]> {
  const map = new Map<string, ResolvedPassport[]>();

  const push = (orgCode: string, passport: ResolvedPassport) => {
    const arr = map.get(orgCode);
    if (!arr) {
      map.set(orgCode, [passport]);
    } else if (!arr.some((p) => p.passportId === passport.passportId)) {
      arr.push(passport);
    }
  };

  passports.forEach((p) => {
    push(p.detail.organizationCode, { ...p, isConcurrent: false });
    p.detail.concurrentOrganizations?.forEach((c) => {
      // 겸직 소속이 본 소속과 같은 경우 중복 방지
      if (c.organizationCode === p.detail.organizationCode) return;
      push(c.organizationCode, buildConcurrentPassport(p, c));
    });
  });

  // 서버는 본 소속 기준으로 정렬하므로, 겸직 직책으로 치환한 후 조직별로 재정렬한다.
  map.forEach((members, orgCode) => {
    map.set(orgCode, sortOrganizationPassports(members));
  });
  return map;
}
